export type SoundEffect = "confirm" | "hit" | "miss" | "move" | "finish";

const soundFrequencies: Record<SoundEffect, readonly [number, number]> = {
  confirm: [520, 760],
  hit: [330, 470],
  miss: [180, 120],
  move: [240, 280],
  finish: [440, 880],
};

export class AudioController {
  private context: AudioContext | null = null;
  private muted = false;

  isMuted(): boolean {
    return this.muted;
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
  }

  async unlock(): Promise<void> {
    this.context ??= new AudioContext();
    if (this.context.state === "suspended") await this.context.resume();
  }

  play(effect: SoundEffect): void {
    if (this.muted || !this.context || this.context.state !== "running") return;

    const now = this.context.currentTime;
    const [start, end] = soundFrequencies[effect];
    const oscillator = this.context.createOscillator();
    const gain = this.context.createGain();
    oscillator.type = effect === "miss" ? "sawtooth" : "sine";
    oscillator.frequency.setValueAtTime(start, now);
    oscillator.frequency.exponentialRampToValueAtTime(Math.max(end, 1), now + 0.12);
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.09, now + 0.015);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.16);
    oscillator.connect(gain);
    gain.connect(this.context.destination);
    oscillator.start(now);
    oscillator.stop(now + 0.18);
  }

  async destroy(): Promise<void> {
    if (!this.context) return;
    await this.context.close();
    this.context = null;
  }
}
